// src/components/listing/specialized/PropertiesForm.jsx
import React from 'react';
import Input from '../../../common/Input';
import Dropdown from '../../../common/Dropdown';

const PropertiesForm = ({ form, errors, submitting, onFieldChange, categoryId }) => {
  
  const renderSimpleItem = (item) => (
    <div className="p-3 hover:bg-gray-50 cursor-pointer">
      <span className="text-gray-900">{item.name || item.label}</span>
    </div>
  );

  // Determine which fields to show based on property type
  const showBedroomsBathrooms = ['apartment', 'house', 'short_let', 'hostel'].includes(form.property_type);
  const showCommercialFields = ['commercial', 'office', 'shop'].includes(form.property_type);
  const showLandFields = form.property_type === 'land';
  const showFloors = ['apartment', 'house', 'commercial', 'office'].includes(form.property_type);
  const showFurnishing = ['apartment', 'house', 'short_let', 'office'].includes(form.property_type);

  return (
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Property Details</h4>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Property Type *
        </label>
        <Dropdown
          value={form.property_type || 'apartment'}
          onValueChange={(value) => onFieldChange('property_type', value)}
          items={[
            { id: 'apartment', name: 'Apartment', value: 'apartment' },
            { id: 'house', name: 'House', value: 'house' },
            { id: 'land', name: 'Land', value: 'land' },
            { id: 'commercial', name: 'Commercial', value: 'commercial' },
            { id: 'office', name: 'Office', value: 'office' },
            { id: 'shop', name: 'Shop', value: 'shop' },
            { id: 'hostel', name: 'Hostel', value: 'hostel' },
            { id: 'short_let', name: 'Short Let', value: 'short_let' },
          ]}
          disabled={submitting}
          renderItem={renderSimpleItem}
        />
      </div>

      {/* Bedrooms, Bathrooms, Toilets - only for residential types */}
      {showBedroomsBathrooms && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bedrooms
            </label>
            <Input
              type="number"
              placeholder="0"
              value={form.bedrooms || ''}
              onChange={(e) => onFieldChange('bedrooms', e.target.value)}
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bathrooms
            </label>
            <Input
              type="number"
              placeholder="0"
              value={form.bathrooms || ''}
              onChange={(e) => onFieldChange('bathrooms', e.target.value)}
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Toilets
            </label>
            <Input
              type="number"
              placeholder="0"
              value={form.toilets || ''}
              onChange={(e) => onFieldChange('toilets', e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>
      )}

      {/* Area Size and Unit - always show but adjust placeholder for land */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {showLandFields ? 'Land Size' : 'Area Size'}
          </label>
          <Input
            type="number"
            placeholder={showLandFields ? "e.g., 500" : "e.g., 120"}
            value={form.area_size || ''}
            onChange={(e) => onFieldChange('area_size', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Area Unit
          </label>
          <Dropdown
            value={form.area_unit || (showLandFields ? 'plot' : 'sqm')}
            onValueChange={(value) => onFieldChange('area_unit', value)}
            items={[
              { id: 'sqm', name: 'Square Meters', value: 'sqm' },
              { id: 'sqft', name: 'Square Feet', value: 'sqft' },
              { id: 'plot', name: 'Plot', value: 'plot' },
              { id: 'acre', name: 'Acre', value: 'acre' },
              { id: 'hectare', name: 'Hectare', value: 'hectare' },
            ]}
            disabled={submitting}
            renderItem={renderSimpleItem}
          />
        </div>
      </div>

      {/* Furnishing - only for furnished property types */}
      {showFurnishing && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Furnishing
          </label>
          <Dropdown
            value={form.furnishing || 'unfurnished'}
            onValueChange={(value) => onFieldChange('furnishing', value)}
            items={[
              { id: 'furnished', name: 'Furnished', value: 'furnished' },
              { id: 'semi-furnished', name: 'Semi-Furnished', value: 'semi-furnished' },
              { id: 'unfurnished', name: 'Unfurnished', value: 'unfurnished' },
            ]}
            disabled={submitting}
            renderItem={renderSimpleItem}
          />
        </div>
      )}

      {/* Parking Spaces - show for all except land */}
      {!showLandFields && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Parking Spaces (Optional)
            </label>
            <Input
              type="number"
              placeholder="e.g., 2"
              value={form.parking_spaces || ''}
              onChange={(e) => onFieldChange('parking_spaces', e.target.value)}
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Charge (₦/month)
            </label>
            <Input
              type="number"
              placeholder="e.g., 5000"
              value={form.service_charge || ''}
              onChange={(e) => onFieldChange('service_charge', e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>
      )}

      {/* Floor Information - only for multi-story properties */}
      {showFloors && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Floor Number
            </label>
            <Input
              type="number"
              placeholder="e.g., 3"
              value={form.floor_number || ''}
              onChange={(e) => onFieldChange('floor_number', e.target.value)}
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Total Floors
            </label>
            <Input
              type="number"
              placeholder="e.g., 10"
              value={form.total_floors || ''}
              onChange={(e) => onFieldChange('total_floors', e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>
      )}

      {/* Land-specific fields */}
      {showLandFields && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Topography
          </label>
          <Dropdown
            value={form.topography || ''}
            onValueChange={(value) => onFieldChange('topography', value)}
            items={[
              { id: 'flat', name: 'Flat', value: 'flat' },
              { id: 'sloping', name: 'Sloping', value: 'sloping' },
              { id: 'hilly', name: 'Hilly', value: 'hilly' },
            ]}
            disabled={submitting}
            renderItem={renderSimpleItem}
          />
        </div>
      )}

      {/* Property Status - show for all types */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Property Status
        </label>
        <Dropdown
          value={form.property_status || 'for sale'}
          onValueChange={(value) => onFieldChange('property_status', value)}
          items={[
            { id: 'for sale', name: 'For Sale', value: 'for sale' },
            { id: 'for lease', name: 'For Lease', value: 'for lease' },
            { id: 'for rent', name: 'For Rent', value: 'for rent' },
            { id: 'off-plan', name: 'Off-Plan', value: 'off-plan' },
          ]}
          disabled={submitting}
          renderItem={renderSimpleItem}
        />
      </div>
    </div>
  );
};

export default PropertiesForm;